import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

// Translation dictionary — French is the default
const DICT = {
  fr: {
    "nav.story": "Histoire",
    "nav.menu": "Menu",
    "nav.vibe": "Ambiance",
    "nav.find": "Nous Trouver",
    "nav.cta": "Découvrir le Menu",

    "hero.kicker": "Est. 2025",
    "hero.headline.1": "Bonjour",
    "hero.toast": "Toast.",
    "hero.subhead":
      "Croque-monsieurs à la française, faits main et servis chauds. Une indulgence française, préparée avec amour — parce que la vie est meilleure toastée.",
    "hero.cta.menu": "Découvrir le Menu",
    "hero.cta.find": "Nous Trouver",
    "hero.scroll": "Défiler",
    "hero.side": "La vie est meilleure toastée.",

    "story.kicker": "— Notre Histoire",
    "story.heading.1": "Un petit morceau de",
    "story.heading.france": "France",
    "story.heading.2": "à savourer, partout dans le monde.",
    "story.body.1":
      "Monsieur Toast est né d'une obsession française très simple : le",
    "story.body.croque": "croque-monsieur",
    "story.body.2":
      ", pris au sérieux. Brioche cuite chaque matin, Comté affiné fondu lentement, et chaque sandwich pressé jusqu'à devenir doré — comme il se doit.",
    "story.body.3":
      "Aujourd'hui, nous ouvrons notre premier comptoir. Demain, le monde. Une bouchée parfaitement toastée à la fois.",
    "story.badge": "Fait avec amour",
    "story.stat.bread": "Cuisson lente",
    "story.stat.cheese": "Fromage français",
    "story.stat.craft": "Pressé à la carte",

    "showcase.kicker": "— Signature Maison",
    "showcase.title.1": "Monsieur",
    "showcase.title.2": "toast",
    "showcase.subtitle": "Légumes grillés & fromage fondant",
    "showcase.intro": "Un classique revisité avec élégance.",
    "showcase.badge.1": "Gourmand, fondant",
    "showcase.badge.2": "et 100% plaisir",
    "showcase.feature.1": "Ingrédients frais et de saison",
    "showcase.feature.2": "Fait maison avec amour",
    "showcase.feature.3": "À déguster sur place ou à emporter",

    "menu.kicker": "— Le Menu",
    "menu.heading.1": "Toasts",
    "menu.heading.2": "Signature",
    "menu.intro":
      "Sept recettes. Françaises jusqu'à la mie. Chaque toast pressé à la commande sur notre pain au levain ou brioche au beurre.",
    "menu.footnote": "Tous les prix en euros — TVA incluse",

    "vibe.marquee": "La vie est meilleure toastée",
    "vibe.counter.kicker": "Le Comptoir",
    "vibe.counter.title": "Où le savoir-faire français rencontre votre quotidien.",
    "vibe.first.title.1": "Mais d'abord,",
    "vibe.first.title.2": "un toast.",
    "vibe.first.body":
      "Une pause dans votre journée. Une croûte chaude. Un fromage qui file. Voilà l'instant Monsieur Toast.",
    "vibe.first.foot": "— Tous les jours, toute la journée",
    "vibe.manifesto.kicker": "— Manifeste",
    "vibe.manifesto.1": "Une âme",
    "vibe.manifesto.2": "française,",
    "vibe.manifesto.3": "partagée avec le monde.",

    "find.kicker": "— Nous Trouver",
    "find.heading.1": "Venez nous dire",
    "find.heading.bonjour": "bonjour",
    "find.body":
      "Nous ouvrons bientôt nos premiers comptoirs. Pour la presse, un partenariat ou une demande de franchise, écrivez-nous — nous lisons chaque message nous-mêmes.",
    "find.badge": "Une enseigne en pleine création",
    "find.email": "Email",
    "find.phone": "Téléphone",
    "find.address": "Adresse",
    "find.address.detail": "Emplacement à venir",
    "find.follow": "Nous suivre",

    "form.kicker": "— Nous Écrire",
    "form.heading": "Un message, et on s'occupe du reste.",
    "form.sub": "Nom, email, message. Rien de plus.",
    "form.name": "Votre nom",
    "form.email": "Votre email",
    "form.message": "Votre message",
    "form.submit": "Envoyer",
    "form.sending": "Envoi…",
    "form.success": "Message envoyé — nous vous répondons très vite.",
    "form.error": "L'envoi a échoué. Réessayez ou écrivez-nous directement par email.",

    "footer.tagline": "Une signature française",
    "footer.follow": "Suivez-nous",
    "footer.sections": "Sections",
    "footer.contact": "Contact",
    "footer.about":
      "Croque-monsieurs à la française, pressés main. Bientôt ouvert.",
    "footer.rights": "Tous droits réservés.",
    "footer.slogan": "La vie est meilleure toastée.",

    "lang.toggle": "EN",
  },
  en: {
    "nav.story": "Story",
    "nav.menu": "Menu",
    "nav.vibe": "Vibe",
    "nav.find": "Find Us",
    "nav.cta": "Taste the Menu",

    "hero.kicker": "Est. 2025",
    "hero.headline.1": "Bonjour",
    "hero.toast": "Toast.",
    "hero.subhead":
      "Hand-crafted French toasted sandwiches, served warm. A little French indulgence, made with love — because life is better toasted.",
    "hero.cta.menu": "Discover the Menu",
    "hero.cta.find": "Find Us",
    "hero.scroll": "Scroll",
    "hero.side": "Life is better toasted.",

    "story.kicker": "— Our Story",
    "story.heading.1": "A little piece of",
    "story.heading.france": "France",
    "story.heading.2": "to savour, wherever you are.",
    "story.body.1": "Monsieur Toast was born from a simple French obsession: the humble",
    "story.body.croque": "croque-monsieur",
    "story.body.2":
      ", taken seriously. We bake our brioche fresh, melt aged Comté slowly, and press every sandwich golden — the way it should be.",
    "story.body.3":
      "Today, we open our first counter. Tomorrow, the world. One perfectly toasted bite at a time.",
    "story.badge": "Made with love",
    "story.stat.bread": "Slow-baked",
    "story.stat.cheese": "French cheese",
    "story.stat.craft": "Pressed à la carte",

    "showcase.kicker": "— House Signature",
    "showcase.title.1": "Monsieur",
    "showcase.title.2": "toast",
    "showcase.subtitle": "Grilled vegetables & melted cheese",
    "showcase.intro": "A classic, revisited with elegance.",
    "showcase.badge.1": "Indulgent, melting",
    "showcase.badge.2": "and 100% pleasure",
    "showcase.feature.1": "Fresh, seasonal ingredients",
    "showcase.feature.2": "Handmade with love",
    "showcase.feature.3": "Eat in or take away",

    "menu.kicker": "— The Menu",
    "menu.heading.1": "Signature",
    "menu.heading.2": "Toasts",
    "menu.intro":
      "Seven recipes. French to the crumb. Each toast pressed to order on our slow-baked sourdough or buttered brioche.",
    "menu.footnote": "All prices in AED — VAT included",

    "vibe.marquee": "Life is better toasted",
    "vibe.counter.kicker": "The Counter",
    "vibe.counter.title": "Where French savoir-faire meets your every day.",
    "vibe.first.title.1": "But first,",
    "vibe.first.title.2": "toast.",
    "vibe.first.body":
      "A pause in your day. A warm crust. A cheese that pulls. That's the Monsieur Toast moment.",
    "vibe.first.foot": "— Daily, all day",
    "vibe.manifesto.kicker": "— Manifesto",
    "vibe.manifesto.1": "A French",
    "vibe.manifesto.2": "Soul,",
    "vibe.manifesto.3": "shared with the world.",

    "find.kicker": "— Find Us",
    "find.heading.1": "Come say",
    "find.heading.bonjour": "bonjour",
    "find.body":
      "We are opening our first counters soon. For press, partnership or franchise enquiries, drop us a line — we read every message ourselves.",
    "find.badge": "A chain in the making",
    "find.email": "Email",
    "find.phone": "Phone",
    "find.address": "Address",
    "find.address.detail": "First openings to come",
    "find.follow": "Follow",

    "form.kicker": "— Write to Us",
    "form.heading": "One message, we'll take it from there.",
    "form.sub": "Name, email, message. Nothing more.",
    "form.name": "Your name",
    "form.email": "Your email",
    "form.message": "Your message",
    "form.submit": "Send",
    "form.sending": "Sending…",
    "form.success": "Message sent — we'll get back to you very soon.",
    "form.error": "Sending failed. Please try again or email us directly.",

    "footer.tagline": "A French Signature",
    "footer.follow": "Follow us",
    "footer.sections": "Sections",
    "footer.contact": "Contact",
    "footer.about":
      "French toasted sandwiches, hand-pressed. Coming soon.",
    "footer.rights": "All rights reserved.",
    "footer.slogan": "Life is better toasted.",

    "lang.toggle": "FR",
  },
};

const LanguageContext = createContext({
  lang: "fr",
  setLang: () => {},
  toggle: () => {},
  t: (k) => k,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "fr";
    const saved = window.localStorage.getItem("mt-lang");
    if (saved === "en" || saved === "fr") return saved;
    // Detect browser language on first visit
    const browserLang = (navigator.language || navigator.userLanguage || "fr").toLowerCase();
    return browserLang.startsWith("fr") ? "fr" : "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mt-lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((v) => setLangState(v === "en" ? "en" : "fr"), []);
  const toggle = useCallback(
    () => setLangState((prev) => (prev === "fr" ? "en" : "fr")),
    []
  );

  const t = useCallback(
    (key) => {
      const table = DICT[lang] || DICT.fr;
      return table[key] ?? DICT.fr[key] ?? key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, setLang, toggle, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
